import React from 'react'

import Reflux from 'reflux-react-16'
import moment from 'moment'
import _ from 'lodash'
import aSync from 'async'

import ParcelStore from '../../stores/ParcelStore.js'
import ParcelActions from '../../actions/ParcelActions.js'
import AddressStore from '../../stores/AddressStore.js'

import ViewParcelModal from '../../components/parcels/ViewParcelModal.js'
import EditParcelModal from '../../components/parcels/EditParcelModal.js'
import ImportParcels from '../../components/parcels/ImportParcels.js'
import {fromLabelCallback, printLabels} from '../../components/labels/printLabels.js'
import NewParcel from '../../../server/functions/parcels/NewParcel.js'
import {hasNoTrackTrace} from '../../../server/functions/parcels/trackTrace.js'
import {Button, Colors, SplitButton, IconButton, S2, P, Panel, Popup, PdfModal, Icon, ToolTip} from '../../components/UI/index.js'
import CarrierLogo from '../../components/parcels/CarrierLogo.js'
import ChangeParcelStatusPopup from '../../components/parcels/ChangeParcelStatusPopup.js'
import Table from '../../components/UI/table/index.js'
import SearchBar from '../../components/SearchBar/index.js'
import dbCodes from '../../../server/dbCodes.js'
import csvExport from '../../utils/csvExport.js'
import filter from '../../utils/filter.js'
import webshopNames from '../../utils/webshopNames.js'
import {formatPrice, parsePrice} from '../../../utils/price.js'
import t from 'src/helpers/Languages.js'
import {formSpacing} from '../../components/UI/forms/index.js'
import createMemo from '../../utils/createMemo.js'

const addressSortOptions = ['name', 'street', 'nr', 'postalCode', 'city']

/**
 * The Adres column used to read and store its sort option in the customer orders' table settings (_dashboard), so the
 * option chosen there applies until one is chosen in this table
 */
function legacyAddressSortOption() {
    try {
        return JSON.parse(localStorage._dashboard || '{}')?.addressSortOption
    } catch {
        return undefined
    }
}

class Parcels extends Reflux.Component {
    constructor(props) {
        super(props)

        this.columnsMemo = createMemo()

        this.state = {printerPageSize: localStorage.printerPageSize || 'A6'}

        this.stores = [ParcelStore, AddressStore]
    }

    componentDidUpdate() {
        if (this.state.parcelLimitWarning) {
            this.setState({parcelLimitWarning: false})
            this.alertPopup.openAlert('Waarschuwing', 'Je huidige datumrange bevat meer dan 35 000 zendingen, hierdoor worden niet alle zendingen weergegeven. Verklein de datumrange om dit te voorkomen.')
        }
    }

    componentDidMount() {
        const {user} = this.props
        const {parcelEndDate} = this.state

        if (user.settings.parcels?.defaultDateRange === 'week') {
            const parcelStartDate = moment(parcelEndDate).subtract(1, 'week').format('YYYY-MM-DD')
            ParcelActions.setDates(parcelStartDate, parcelEndDate)
        }

        if (user.settings.parcels?.defaultDateRange === 'month') {
            const parcelStartDate = moment(parcelEndDate).subtract(30, 'days').format('YYYY-MM-DD')
            ParcelActions.setDates(parcelStartDate, parcelEndDate)
        }

        if (user.settings.parcels?.defaultDateRange === '90days') {
            const parcelStartDate = moment(parcelEndDate).subtract(90, 'days').format('YYYY-MM-DD')
            ParcelActions.setDates(parcelStartDate, parcelEndDate)
        }

        if (user.settings.parcels?.defaultDateRange === 'year') {
            const parcelStartDate = moment(parcelEndDate).subtract(365, 'days').format('YYYY-MM-DD')
            ParcelActions.setDates(parcelStartDate, parcelEndDate)
        }
    }

    // The same columns until a value they read changes: new columns re-render every row of the table
    columns() {
        return this.columnsMemo([this.state.carriers], () => this.buildColumns())
    }

    buildColumns() {
        return [
            {id: 'date', title: 'Datum', visible: true, width: 120, key: 'date', sort: (parcel) => {
                return `${parcel.date} ${parcel.timeConfirmed}`
            }, render: (parcel) => {
                return (
                    <div style={{width: '100%'}}>
                        <P ellipsis>{parcel.date ? moment(parcel.date).format('DD-MM-YYYY') : ''}</P>
                        <S2 ellipsis>{parcel.timeConfirmed}</S2>
                    </div>
                )
            }},
            {id: 'address', title: 'Adres', visible: true, sort: (parcel, option) => {
                return parcel.address[option]
            }, dropdown: {
                defaultValue: addressSortOptions.includes(legacyAddressSortOption()) ? legacyAddressSortOption() : 'postalCode',
                options: [
                    {
                        label: 'Naam',
                        value: 'name'
                    },
                    {
                        label: 'Straat',
                        value: 'street'
                    },
                    {
                        label: 'Huisnummer',
                        value: 'nr'
                    },
                    {
                        label: 'Postcode',
                        value: 'postalCode'
                    },
                    {
                        label: 'Plaats',
                        value: 'city'
                    }
                ]
            }, flex: 2, render: (parcel) => {
                const address = parcel.address

                return (
                    <div style={{width: '100%'}}>
                        <P ellipsis>{address.name}</P>
                        <div style={{display: 'flex', alignItems: 'center'}}>
                            {address.postalCode && address.country === 'NL' && address.showWarning &&
                                <ToolTip text='Adres niet gevonden. Controleer de gegevens voor je bevestigt.'>
                                    <Icon size='md' style={{marginRight: 6, color: Colors.errorBright}} icon='mdi mdi-alert' />
                                </ToolTip>
                            }
                            <S2 ellipsis>{`${address.street} ${address.nr}${address.addition} ${address.postalCode} ${address.city}`}</S2>
                        </div>
                    </div>
                )
            }},
            {id: 'address.country', title: 'Land', visible: true, width: 75, key: 'address.country'},
            {id: 'reference', title: 'Referentie', visible: true, width: 200, key: 'reference'},
            {id: 'comment', title: 'Bezorginstructie', visible: false, flex: 1, key: 'comment'},
            {id: 'options', title: 'Opties', visible: true, width: 250, key: 'options', text: (parcel) => {
                return parcel.options.join(', ')
            }},
            {id: 'weight', title: 'Gewicht', visible: false, width: 100, key: 'weight', text: (parcel) => {
                return `${parcel.weight || 0} g`
            }},
            {id: 'price', title: 'Prijs', visible: false, width: 100, key: 'price', sort: (parcel) => {
                let price = parsePrice(parcel.price)

                parcel.fees.map((fee) => {
                    if (typeof fee.price === 'string' && fee.price) {
                        price += parsePrice(fee.price) * fee.quantity
                    }
                })

                return price
            }, text: (parcel) => {
                if (parcel.price === 'priceScale') {
                    if (parcel.priceScaleRange) {
                        return `€ ${parcel.priceScaleRange.min} - € ${parcel.priceScaleRange.max}`
                    }
                    return 'n.t.b.'
                }

                let price = parsePrice(parcel.price)

                parcel.fees.map((fee) => {
                    if (typeof fee.price === 'string' && fee.price) {
                        price += parsePrice(fee.price) * fee.quantity
                    }
                })

                return `€ ${formatPrice(price)}`
            }},
            {id: 'carrier', title: 'Vervoerder', visible: true, width: 150, key: 'carrier', render: (parcel) => {
                return <CarrierLogo size={35} carriers={this.state.carriers} value={parcel.carrier} options={parcel.options} />
            }},
            {id: 'trackTrace', title: 'Track & Trace', visible: true, width: 220, key: 'trackTrace', onClick: (parcel) => {
                if (hasNoTrackTrace(parcel)) {
                    return
                }

                if (parcel.trackTraceLink.indexOf('veloyd') > -1) {
                    window.open(`/track&trace/${parcel.trackTrace}`)
                } else {
                    window.open(parcel.trackTraceLink)
                }
            }},
            {id: 'externId', title: 'Externe ID', visible: false, width: 100, key: 'externId', text: (parcel) => {
                if (['GLS', 'Skynet'].includes(parcel.carrier)) {
                    return parcel.externId
                }
            }},
            {id: 'plugin', title: 'Koppeling', visible: false, width: 150, key: 'plugin', text: (parcel) => {
                let plugin = parcel.plugin || parcel.webshop || ''

                if (webshopNames[plugin]) {
                    return webshopNames[plugin]
                }

                if (plugin && !['API', 'CSV'].includes(plugin)) {
                    plugin = _.capitalize(plugin)
                }

                return plugin
            }},
            {id: 'status', title: 'Status', visible: true, width: 150, key: 'status', text: (parcel) => {
                return dbCodes.parcelStatus[parcel.status]
            }},
            {id: 'actions', visible: true, showOnHover: true, width: 50, render: (parcel) => {
                if (parcel.status === dbCodes.parcelStatus.aangemaakt()) {
                    return (
                        <IconButton onClick={() => this.editParcelModal.open(parcel)}>
                            <i className='mdi mdi-pencil' />
                        </IconButton>
                    )
                } else {
                    return (
                        <IconButton onClick={() => this.viewParcelModal.open(parcel._id)}>
                            <i className='mdi mdi-eye' />
                        </IconButton>
                    )
                }
            }}
        ]
    }

    onChangeDates(parcelStartDate, parcelEndDate) {
        ParcelActions.setDates(parcelStartDate, parcelEndDate)
    }

    onChangeQueries(parcelQueries) {
        this.table.selectPage(1)
        ParcelActions.setQueries(parcelQueries)
    }

    onChangeTable(selectedParcels) {
        ParcelActions.setSelected(selectedParcels)
    }

    onClickNew() {
        this.editParcelModal.open()
    }

    onClickGetOrdersFromWebshop() {
        this.popup.open('Importeren vanuit webshop', 'Bestellingen in je webshop worden geimporteerd.', () => {
            // The backend tells what it is doing (fetching pages, processing orders, creating parcels)
            this.popup.listenToProgress()

            ParcelActions.getOrdersFromWebshop((err, response) => {
                if (err) {
                    this.popup.setError(err)
                } else {
                    this.popup.setMessage(`${response.ids.length} nieuwe ${response.ids.length === 1 ? 'zending' : 'zendingen'} aangemaakt.`)
                    this.popup.setErrors(response.errors)
                }
            })
        })
    }

    async onClickExport() {
        const {selectedParcels, parcels, parcelStartDate, parcelEndDate, carriers} = this.state

        const parcelsById = _.keyBy(parcels, '_id')
        const selected = selectedParcels.map((id) => parcelsById[id])

        await this.popup.openProgress(t('export'), '', (onProgress) => csvExport.parcels(true, selected, parcelStartDate, parcelEndDate, null, null, carriers, onProgress))

        ParcelActions.setSelected([])
    }

    async onClickCopy(id, multiple, options) {
        if (id) {
            let parcel = _.find(this.state.parcels, {_id: id})

            parcel = JSON.parse(JSON.stringify(parcel))

            const copyParcel = () => {
                const newParcel = NewParcel()

                newParcel.customer = parcel.customer
                newParcel.contact = parcel.contact
                newParcel.address = parcel.address
                newParcel.emailTT = parcel.emailTT
                newParcel.reference = parcel.reference
                newParcel.comment = parcel.comment
                newParcel.weight = parcel.weight
                newParcel.height = parcel.height
                newParcel.width = parcel.width
                newParcel.length = parcel.length
                newParcel.options = options || parcel.options

                if (parcel.cover) {
                    newParcel.cover = parcel.cover
                }
                if (parcel.deliveryTime) {
                    newParcel.deliveryTime = parcel.deliveryTime
                }
                if (parcel.pickupDate) {
                    newParcel.pickupDate = parcel.pickupDate
                }
                if (parcel.pickupAddress) {
                    newParcel.pickupAddress = parcel.pickupAddress
                }
                if (parcel.pickupPoint) {
                    newParcel.pickupPoint = parcel.pickupPoint
                }

                newParcel.carrier = parcel.carrier

                if (parcel.customs) {
                    newParcel.customs = parcel.customs
                }

                return newParcel
            }

            if (multiple) {
                const popupInput = {
                    label: 'Aantal kopieën',
                    value: 1,
                    type: 'number',
                    min: 1,
                    max: 99

                }
                this.popup.setInput(popupInput)
                const inputValue = await this.popup.open('Kopieer meerdere keren', '')
                if (inputValue === undefined) return

                popupInput.value = inputValue
                popupInput.disabled = true
                this.popup.setInput(popupInput)

                this.popup.setProgress(0, inputValue)

                let success = 0
                const errors = []

                for (let i = 0; i < inputValue; i++) {
                    const newParcel = copyParcel()

                    await new Promise((resolve) => {
                        ParcelActions.create(newParcel, (err) => {
                            if (err) {
                                errors.push(err)
                            } else {
                                success += 1
                            }

                            this.popup.setProgress(success + errors.length, inputValue)
                            resolve()
                        })
                    })
                }
                this.popup.setMessage(`${success} ${success === 1 ? 'zending' : 'zendingen'} gekopieerd.`)
                this.popup.setErrors(errors)
            } else {
                const newParcel = copyParcel()

                this.editParcelModal.open(newParcel)
            }

            ParcelActions.setSelected([])
        }
    }

    onClickRemove() {
        const {selectedParcels} = this.state

        this.popup.open('Zendingen verwijderen', 'Zendingen met status Aangemaakt worden verwijderd.', () => {
            ParcelActions.setSelected([])

            let success = 0
            const errors = []

            this.popup.setProgress(0, selectedParcels.length)

            aSync.eachSeries(selectedParcels, (id, next) => {
                ParcelActions.remove(id, (err) => {
                    if (err) {
                        errors.push(err)
                    } else {
                        success += 1
                    }

                    this.popup.setProgress(success + errors.length, selectedParcels.length)

                    next()
                })
            }).then(() => {
                this.popup.setMessage(`${success} ${success === 1 ? 'zending' : 'zendingen'} verwijderd.`)
                this.popup.setErrors(errors)
            })
        })
    }

    onClickCancel() {
        const {selectedParcels} = this.state

        this.popup.open('Zendingen annuleren', 'Zendingen met status Bevestigd worden geannuleerd.', () => {
            ParcelActions.setSelected([])

            let success = 0
            const errors = []

            this.popup.setProgress(0, selectedParcels.length)

            aSync.eachSeries(selectedParcels, (id, next) => {
                ParcelActions.cancel(id, (err) => {
                    if (err) {
                        errors.push(err)
                    } else {
                        success += 1
                    }

                    this.popup.setProgress(success + errors.length, selectedParcels.length)

                    next()
                })
            }).then(() => {
                this.popup.setMessage(`${success} ${success === 1 ? 'zending' : 'zendingen'} geannuleerd.`)
                this.popup.setErrors(errors)
            })
        })
    }

    onClickPrintLabels(id) {
        const {selectedParcels, parcels, printerPageSize} = this.state

        const ids = (id && typeof id === 'string') ? [id] : selectedParcels

        printLabels({
            ids,
            printerPageSize,
            printPopup: this.printPopup,
            pdfModal: this.pdfModal,
            print: (ids, nrOfEmptyLabels) => fromLabelCallback((callback) => ParcelActions.printLabels(ids, nrOfEmptyLabels, callback)),
            text: 'Zendingen worden bevestigd en kunnen niet meer worden gewijzigd.',
            itemName: 'zendingen',
            onPrint: () => ParcelActions.setSelected([]),
            warning: {
                popup: this.popup,
                title: 'Foutieve adressen',
                text: 'Eén of meer van de geselecteerde zendingen heeft een onbekende combinatie van postcode/straatnaam + huisnummer. Wil je toch doorgaan met het printen van de labels?',
                show: () => parcels.filter((parcel) => ids.includes(parcel._id)).some(({address}) => address.postalCode && address.country === 'NL' && address.showWarning)
            },
            batchProgressMessages: false
        })
    }

    onChangePrinterPageSize(event) {
        localStorage.printerPageSize = event.target.value
        this.setState({printerPageSize: event.target.value})
    }

    onOpenChangeParcelStatus() {
        const {selectedParcels} = this.state
        this.changeParcelStatusPopup.open((newStatus) => {
            this.setState({loading: true})
            let success = 0
            const errors = []

            this.changeParcelStatusPopup.setProgress(0, selectedParcels.length)

            ParcelActions.setSelected([])
            aSync.eachSeries(selectedParcels, (id, next) => {
                if (this.changeParcelStatusPopup?.state?.stopOperation) return
                ParcelActions.changeStatus(id, newStatus, (err) => {
                    if (err) {
                        errors.push(err)
                    } else {
                        success += 1
                    }

                    this.changeParcelStatusPopup.setProgress(success + errors.length, selectedParcels.length)

                    next()
                })
            }).then(() => {
                this.changeParcelStatusPopup.setMessage(`${success} ${success === 1 ? 'zending' : 'zendingen'} gewijzigd.`)
                this.changeParcelStatusPopup.setErrors(errors)
            })
        })
    }

    async onClickAddOptions() {
        const {selectedParcels} = this.state
        const {reseller} = this.props

        let enabledOptions = (_.keys(reseller.settings.parcels.products) || []).filter((product) => {
            return _.some(_.keys(reseller.settings.parcels.products[product]), (carrier) => {
                return reseller.settings.parcels.products[product][carrier]
            })
        }).sort()

        // filter out postbus and antwoordnummer
        enabledOptions = enabledOptions.filter((option) => !['Postbus', 'Antwoordnummer'].includes(option))

        enabledOptions = enabledOptions.map((option) => {
            return {value: option, title: option}
        })

        this.popup.setInputs([
            {
                label: 'Verzendopties',
                info: 'Als een combinatie van opties niet mogelijk is wordt de voorkeur gegeven aan de eerst geselecteerde optie.',
                noSort: true,
                name: 'options',
                values: [],
                type: 'select',
                options: enabledOptions
            },
            {
                label: 'Verzendopties vervangen',
                name: 'replace',
                value: false,
                type: 'checkbox'
            }
        ])

        const values = await this.popup.open('Verzendopties toevoegen', 'Kies de verzendopties die toegevoegd moeten worden.')
        if (!values) return
        ParcelActions.setSelected([])

        const options = values.options
        const replace = !!values.replace

        let success = 0
        const errors = []
        const warnings = []

        this.popup.setProgress(0, selectedParcels.length)

        aSync.eachSeries(selectedParcels, (id, next) => {
            ParcelActions.addOptions(id, options, replace, (err) => {
                if (err) {
                    errors.push(err)
                } else {
                    success += 1
                }

                this.popup.setProgress(success + errors.length, selectedParcels.length)

                next()
            })
        }).then(() => {
            this.popup.setMessage(`${success} ${success === 1 ? 'zending' : 'zendingen'} bewerkt.`)
            this.popup.setErrors(errors)
            this.popup.setWarnings(warnings)
        })
    }

    render() {
        const {parcelStartDate, parcelEndDate, selectedParcels, parcelsLoading, parcelQueries, carriers, addressBook, printerPageSize} = this.state
        const {reseller, user, history} = this.props
        const parcels = filter.parcels(this.state.parcels, parcelQueries, carriers, reseller)

        return (
            <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                <Panel style={{display: 'flex', columnGap: formSpacing.columnGap, borderTop: 'none', borderLeft: 'none', borderRight: 'none', background: Colors.backgroundNeutral, padding: '20px 10px 10px 10px'}}>

                    <SearchBar
                        columns={this.columns()}
                        onQueryChange={this.onChangeQueries.bind(this)}
                        onDateChange={this.onChangeDates.bind(this)}
                        startDate={parcelStartDate}
                        endDate={parcelEndDate}
                        queries={parcelQueries}
                    />

                    <Button
                        variant='outline-white'
                        onClick={this.onClickNew.bind(this)}
                    >
                        Nieuwe Zending
                    </Button>

                    {user.settings.webshops && Object.keys(user.settings.webshops).some((webshop) => user.settings.webshops[webshop].active) &&
                        <Button
                            variant='outline-white'
                            onClick={this.onClickGetOrdersFromWebshop.bind(this)}
                            tooltip='Importeer vanuit webshop'
                        >
                            <i className='mdi mdi-refresh' />
                        </Button>
                    }

                    <Button
                        variant='outline-white'
                        onClick={() => this.importParcelsModal.open()}
                        tooltip='Importeer zendingen'
                    >
                        <i className='mdi mdi-file-import' />
                    </Button>

                    {selectedParcels.length > 0 &&
                        <>

                            <Button
                                variant='outline-white'
                                onClick={this.onClickExport.bind(this)}
                                tooltip='Exporteer zendingen'
                            >
                                <i className='mdi mdi-file-export' />
                            </Button>

                            {selectedParcels.length === 1 &&
                                <>
                                    <SplitButton
                                        variant='outline-white'
                                        onClick={() => this.onClickCopy(selectedParcels[0], false)}
                                        tooltip='Kopieer zending'
                                        options={[
                                            {title: 'Kopieer meerdere keren', action: () => {
                                                this.onClickCopy(selectedParcels[0], true)
                                            }}
                                        ]}
                                    >
                                        <i className='mdi mdi-content-copy' />
                                    </SplitButton>

                                    <Button
                                        variant='outline-white'
                                        tooltip='Retourneer zending'
                                        onClick={() => {
                                            this.onClickCopy(selectedParcels[0], false, ['Retour'])
                                        }}
                                        icon='mdi mdi-keyboard-return'
                                    />
                                </>
                            }

                            <Button
                                variant='outline-white'
                                onClick={() => this.onClickAddOptions()}
                                tooltip='Verzendopties toevoegen'
                            >
                                <i className='mdi mdi-package-variant-closed' />
                            </Button>

                            <Button
                                variant='outline-white'
                                onClick={this.onClickRemove.bind(this)}
                                tooltip='Verwijder zendingen'
                            >
                                <i className='mdi mdi-delete' />
                            </Button>

                            <Button
                                variant='outline-white'
                                onClick={this.onClickCancel.bind(this)}
                                tooltip='Annuleer zendingen'
                            >
                                <i className='mdi mdi-close-circle' />
                            </Button>

                            <SplitButton
                                variant='outline-white'
                                value={printerPageSize}
                                options={[
                                    {title: 'Papierformaat A6', value: 'A6'},
                                    {title: 'Papierformaat 10x21', value: '10x21'},
                                    {title: 'Papierformaat A5', value: 'A5'},
                                    {title: 'Papierformaat A4 | 4x A6', value: 'A4'},
                                    {title: 'Papierformaat A4 | 3x 10x21', value: 'A410x21'},
                                    {title: 'Papierformaat A4 | 2x A5', value: 'A4A5'}
                                ]}
                                onChange={this.onChangePrinterPageSize.bind(this)}
                                onClick={this.onClickPrintLabels.bind(this)}
                                tooltip='Print zending labels'
                            >
                                <i className='mdi mdi-printer' />
                            </SplitButton>

                            <Button
                                variant='outline-white'
                                onClick={this.onOpenChangeParcelStatus.bind(this)}
                            >
                                Status wijzigen
                            </Button>
                        </>
                    }
                </Panel>

                <div style={{flex: 1, marginTop: 24, marginRight: 24, marginLeft: 24}}>
                    <Table
                        tableName='customerDashboard'
                        columns={this.columns()}
                        rows={parcels}
                        selectedRows={selectedParcels}
                        loading={parcelsLoading}
                        onChange={this.onChangeTable.bind(this)}
                        ref={(ref) => this.table = ref}
                    />
                </div>

                <ImportParcels
                    reseller={reseller}
                    user={user}
                    carriers={carriers}
                    ref={(ref) => this.importParcelsModal = ref}
                />

                <ViewParcelModal
                    history={history}
                    match={this.props.match}
                    reseller={reseller}
                    user={user}
                    carriers={carriers}
                    onClickPrintLabels={this.onClickPrintLabels.bind(this)}
                    onClickCopy={this.onClickCopy.bind(this)}
                    ref={(modal) => this.viewParcelModal = modal}
                />

                <EditParcelModal
                    reseller={reseller}
                    user={user}
                    carriers={carriers}
                    addressBook={addressBook}
                    pdfModal={this.pdfModal}
                    ref={(modal) => this.editParcelModal = modal}
                />

                <Popup ref={(modal) => this.popup = modal} />
                <Popup ref={(modal) => this.alertPopup = modal} />
                <Popup ref={(ref) => this.printPopup = ref} />
                <PdfModal ref={(ref) => this.pdfModal = ref} />
                <ChangeParcelStatusPopup ref={(ref) => this.changeParcelStatusPopup = ref} />
            </div>
        )
    }
}

export default (Parcels)
